import React, { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "../icon-button";

import "./slideshow-preview-zoom-button.css";
import { usePreviewContext } from "../../contexts/preview-context";
import { useSelectedSpread } from "../../hooks/useSelection";
import { fixViewBox } from "./utils/fix-view-box";
import { usePreviewPanning } from "./hooks/use-preview-pan-pinch";
import { ZoomIcon, ZoomInIcon, ZoomOutIcon } from "../../icons/icons";
import { useKeyDown } from "../../hooks/useKeyboard";
import { useHasFinePointer } from "../../hooks/use-has-fine-pointer";

export function useZoomButtons() {
	const spread = useSelectedSpread();
	const { calcViewBoxInfo, slideShowPreviewRef } = usePreviewContext();
	/** Controls when to stop RAF loop */
	const zoomingRef = useRef(false);

	/** Keep zoom center when zooming in and out */
	const zoomCenterRef = useRef<eXpress.math.IPoint | undefined>(undefined);

	const getZoomedViewBox = useCallback(
		(factor: number, setInitialZoomCenter = false) => {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			// When called from event before react rerender, spread can still be the old detached one
			if (!spread?.hasOwner()) return undefined;

			// Unzoomed viewBox
			const fullViewBox = calcViewBoxInfo(spread, undefined).viewBox;

			// Current viewBox
			const currentViewBox = slideShow.viewBox
				? eXpress.math.rect(slideShow.viewBox)
				: fullViewBox;
			if (!currentViewBox || !fullViewBox) return undefined;

			// Remember centered location
			if (setInitialZoomCenter && !zoomCenterRef.current) {
				zoomCenterRef.current = currentViewBox.center();
			}

			// Scale new viewBox
			let newViewBox = currentViewBox.scale(1 / factor, 1 / factor);

			// Move center on original center
			newViewBox = newViewBox.at(
				eXpress.math
					.point(zoomCenterRef.current ?? currentViewBox.center())
					.sub(newViewBox.width / 2, newViewBox.height / 2),
			);

			const newViewBox2 = fixViewBox(newViewBox, fullViewBox) ?? currentViewBox;

			return {
				currentViewBox,
				newViewBox: newViewBox2,
			};
		},
		[calcViewBoxInfo, slideShowPreviewRef, spread],
	);

	const canZoom = useCallback(() => {
		const slideShow = slideShowPreviewRef.current?.slideShow;
		const slideShowItem = slideShow ? slideShow.items[slideShow.index] : undefined;

		const getScale = (viewBox?: eXpress.math.Rect) => {
			if (!slideShow || !slideShowItem || !viewBox) return undefined;
			return slideShow.calcViewBoxScale(slideShowItem, {
				newViewBox: viewBox,
				newPortalPaddings: slideShow.portalPaddings,
				newViewBoxMargins: slideShow.viewBoxMargins,
			});
		};

		const zoomInInfo = getZoomedViewBox(1.01);
		const canZoomIn = zoomInInfo
			? (() => {
					const currentScale = getScale(zoomInInfo.currentViewBox);
					const newScale = getScale(zoomInInfo.newViewBox);
					if (currentScale === undefined || newScale === undefined) {
						return !eXpress.math.Rect.isSimilar(
							zoomInInfo.currentViewBox,
							zoomInInfo.newViewBox,
						);
					}
					return newScale > currentScale + 1e-3;
			  })()
			: true;

		const zoomOutInfo = getZoomedViewBox(0.99);
		const canZoomOut = zoomOutInfo
			? (() => {
					const currentScale = getScale(zoomOutInfo.currentViewBox);
					const newScale = getScale(zoomOutInfo.newViewBox);
					if (currentScale === undefined || newScale === undefined) {
						return !eXpress.math.Rect.isSimilar(
							zoomOutInfo.currentViewBox,
							zoomOutInfo.newViewBox,
						);
					}
					return newScale < currentScale - 1e-3;
			  })()
			: true;

		return { canZoomIn, canZoomOut };
	}, [getZoomedViewBox, slideShowPreviewRef]);

	const [{ canZoomIn, canZoomOut }, setCanZoom] = useState(() => canZoom());

	// Show when fine pointer (mouse) is available (don't use touch, when touch + mouse available, also show)
	const showZoomButton = useHasFinePointer();

	// Set initial zoom state on Mount, before onTransformChange is called
	useEffect(() => {
		setCanZoom((prevState) => {
			const nextState = canZoom();
			if (
				prevState.canZoomIn === nextState.canZoomIn &&
				prevState.canZoomOut === nextState.canZoomOut
			) {
				return prevState;
			}
			return nextState;
		});
	}, [canZoom]);

	// When user pinches to zoom,update button states
	useEffect(() => {
		return slideShowPreviewRef.current?.slideShow.onTransformChange.subscribe(() => {
			setCanZoom(canZoom());
		});
	}, [canZoom, slideShowPreviewRef]);

	function zoom(factor: number) {
		if (!zoomingRef.current) return;

		const zoomInfo = getZoomedViewBox(factor, true);
		if (!zoomInfo) return;

		const slideShow = slideShowPreviewRef.current?.slideShow;
		if (!slideShow) return;

		// eslint-disable-next-line react-compiler/react-compiler
		slideShow.viewBox = zoomInfo.newViewBox;

		// Stop RAF loop if end is reached
		if (eXpress.math.Rect.isSimilar(zoomInfo.currentViewBox, zoomInfo.newViewBox)) {
			zoomingRef.current = false;
		}

		requestAnimationFrame(() => zoom(factor));
	}

	// Reset zoom center when user panned preview
	const [isPreviewPanning] = usePreviewPanning();
	useEffect(() => {
		zoomCenterRef.current = undefined;
	}, [isPreviewPanning]);

	return {
		zoomInProps: showZoomButton
			? ({
					disabled: !canZoomIn,
					iconName: "zoom-in",
					onPointerDown: (event: React.PointerEvent) => {
						event.currentTarget.setPointerCapture(event.pointerId);
						zoomingRef.current = true;
						zoom(1.01);
					},
					onPointerUp: () => {
						zoomingRef.current = false;
					},
					onKeyDown: (event: React.KeyboardEvent) => {
						if (event.key === " " || event.key === "Enter") {
							event.preventDefault();
							zoomingRef.current = true;
							zoom(1.005);
						}
					},
					onKeyUp: (event: React.KeyboardEvent) => {
						event.preventDefault();
						zoomingRef.current = false;
					},
					children: <ZoomInIcon />,
			  } as const)
			: undefined,
		zoomOutProps: showZoomButton
			? ({
					disabled: !canZoomOut,
					iconName: "zoom-out",
					onPointerDown: (event: React.PointerEvent) => {
						event.currentTarget.setPointerCapture(event.pointerId);
						zoomingRef.current = true;
						zoom(0.99);
					},
					onPointerUp: () => {
						zoomingRef.current = false;
					},
					onKeyDown: (event: React.KeyboardEvent) => {
						if (event.key === " " || event.key === "Enter") {
							event.preventDefault();
							zoomingRef.current = true;
							zoom(0.995);
						}
					},
					onKeyUp: (event: React.KeyboardEvent) => {
						event.preventDefault();
						zoomingRef.current = false;
					},
					children: <ZoomOutIcon />,
			  } as const)
			: undefined,
	};
}

export const SlideshowPreviewZoomButton = () => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { zoomInProps, zoomOutProps } = useZoomButtons();

	// Close on escape
	useKeyDown((event) => {
		if (event.key === "Escape" && isExpanded) {
			event.stopPropagation();
			setIsExpanded(false);
		}
	});

	return (
		<span>
			<IconButton
				elevated
				onClick={() => {
					// When Zoom opened (or closed), reset zoom center
					//	zoomCenterRef.current = undefined;
					setIsExpanded((prev) => !prev);
				}}
			>
				<ZoomIcon />
			</IconButton>

			<span
				className={`slideshow-preview-zoom-button__content ${
					isExpanded ? "slideshow-preview-zoom-button__content--open" : ""
				}`}
			>
				{zoomInProps && zoomOutProps ? (
					<span className="slideshow-preview-zoom-button__zoom-container">
						<IconButton
							className="slideshow-preview-zoom-button__zoom-in"
							{...zoomInProps}
						></IconButton>
						<hr className="slideshow-preview-zoom-button__zoom-separator" />
						<IconButton
							className="slideshow-preview-zoom-button__zoom-out"
							{...zoomOutProps}
						></IconButton>
					</span>
				) : null}
			</span>
		</span>
	);
};
